import clsx from "clsx";
import { AnimatePresence, motion } from "motion/react";
import { FC, useEffect } from "react";
import { useRecoilValue } from "recoil";

import { TableWrapperGeneric, useTableDefsRidesMap } from "@ryde/controls";
import { useCompanyScopeContext, useRidesGridSplitter } from "@ryde/redux";
import {
  GlobalAppEventEnum,
  IColumnDef,
  IExtendableDto,
  liveMapMobileSurfaceAtom,
  LiveMapMobileSurfaceEnum,
  RidesViewTypeEnum,
  SPLITTER_MAP_INITIAL_WIDTH_LIVE_MAP,
  useIsMobile,
} from "@ryde/shared";

import { RidesRoutes } from "../../rides/routes";
import { RidesGridTableWrapperStyled } from "../../rides/search/grid";
import { LiveMapMap } from "../map";
import { LiveMapGridMapShellStyled, LiveMapSplitterRootStyled } from "./parts";

export const LiveMapPageInner: FC = () => {
  const { isMobile } = useIsMobile();
  const mobileSurface = useRecoilValue(liveMapMobileSurfaceAtom);
  const { eventEmitter } = useCompanyScopeContext();
  const { tableDefsRidesMap } = useTableDefsRidesMap();
  const isGridSurface = mobileSurface === LiveMapMobileSurfaceEnum.Grid;
  const isMapSurface = mobileSurface === LiveMapMobileSurfaceEnum.Map;

  useEffect(() => {
    if (!isMobile || !isMapSurface) {
      return;
    }

    eventEmitter?.emit(GlobalAppEventEnum.RESIZE_RIDE_MAP);
  }, [eventEmitter, isMapSurface, isMobile]);
  const {
    mapOpen,
    x,
    onDragStartHandler,
    onDragEndHandler,
    gridSizeProps,
    mapSizeProps,
    dragConstraints,
  } = useRidesGridSplitter(
    RidesViewTypeEnum.LiveMap,
    SPLITTER_MAP_INITIAL_WIDTH_LIVE_MAP,
  );

  const tableDefs = tableDefsRidesMap as IColumnDef<IExtendableDto, unknown>[];

  if (isMobile) {
    return (
      <>
        <LiveMapSplitterRootStyled $isMobile>
          <LiveMapGridMapShellStyled>
            <RidesGridTableWrapperStyled>
              <div
                className={clsx("grid_panel mobile_full", {
                  ["is-surface-hidden"]: !isGridSurface,
                })}
                aria-hidden={!isGridSurface}
              >
                <TableWrapperGeneric tableDefs={tableDefs} />
              </div>
              <div
                className={clsx("map_panel mobile_full", {
                  ["is-surface-hidden"]: !isMapSurface,
                })}
                aria-hidden={!isMapSurface}
              >
                <LiveMapMap />
              </div>
            </RidesGridTableWrapperStyled>
          </LiveMapGridMapShellStyled>
        </LiveMapSplitterRootStyled>
        <RidesRoutes />
      </>
    );
  }

  return (
    <>
      <LiveMapSplitterRootStyled>
        <LiveMapGridMapShellStyled>
          <RidesGridTableWrapperStyled>
            <motion.div
              className="grid_panel thin_scrollbar"
              style={gridSizeProps}
            >
              <TableWrapperGeneric tableDefs={tableDefs} />
            </motion.div>
            <AnimatePresence>
              {mapOpen && (
                <motion.div
                  className="splitter"
                  key="live-map-splitter"
                  dragElastic={0}
                  dragMomentum={false}
                  onDragStart={onDragStartHandler}
                  onDragEnd={onDragEndHandler}
                  dragConstraints={dragConstraints}
                  drag="x"
                  style={{
                    cursor: "col-resize",
                    x,
                  }}
                />
              )}
              {mapOpen && (
                <motion.div
                  key="live-map-map-panel"
                  className="map_panel"
                  style={mapSizeProps}
                >
                  <LiveMapMap />
                </motion.div>
              )}
            </AnimatePresence>
          </RidesGridTableWrapperStyled>
        </LiveMapGridMapShellStyled>
      </LiveMapSplitterRootStyled>
      <RidesRoutes />
    </>
  );
};
